import { useQueryClient } from '@tanstack/react-query';
import { noop, uniq, uniqBy, without, xor } from 'lodash-es';
import { createContext, useCallback, useMemo, useState } from 'react';

import { ExpandedState } from '~components/ExpandedStateButton/ExpandedStateButton';
import { Job } from '~hooks/useJob/models';
import { Order } from '~hooks/useOrders/models';
import useOrderCardsExpandedState, {
  DEFAULT_ORDER_CARDS_EXPANDED_STATE,
  OrderCardsExpandedState,
} from '~pages/Approvals/shared/useOrderCardsExpandedState';
import { useStores } from '~store';
import { getApprovalStatus } from '~utils/jobs/job-utils';

import { makeJobsQueryContextKey } from './utils';

type CheckedState = boolean | 'intermediate';

export enum DefaultSelectedTab {
  JOBS = 'jobs',
  LOADS = 'loads',
}

const LOCAL_STORAGE_KEY_DEFAULT_TAB = 'approveJobs_defaultTab';
const LOCAL_STORAGE_KEY_EXPANDED_STATE = 'approveJobs_expandedState';

function getStoredDefaultTab(): `${DefaultSelectedTab}` {
  const stored = localStorage.getItem(LOCAL_STORAGE_KEY_DEFAULT_TAB);
  if (stored === DefaultSelectedTab.LOADS || stored === DefaultSelectedTab.JOBS) {
    return stored;
  }
  return DefaultSelectedTab.JOBS;
}

export type FilesPageContextType = {
  approvableJobs: Job[];
  checkedJobIds: string[];
  defaultSelectedTab: `${DefaultSelectedTab}`;
  getJobCheckedState: (job: Job) => boolean;
  getJobsInOrder: (order: Order) => Job[];
  getOrderCheckedState: (order: Order) => CheckedState;
  orderCardsExpandedState: OrderCardsExpandedState;
  reportJobsLoaded: (jobs: Job[]) => void;
  reset: () => void;
  setDefaultSelectedTab: (tab: `${DefaultSelectedTab}`) => void;
  toggleAllOrderCardsExpandedState: (expandedState: `${ExpandedState}`) => void;
  toggleJobCheck: (job: Job) => void;
  toggleOrderCheck: (order: Order) => void;
  toggleSingleOrderCardsExpandedState: (
    orderId: string,
    expandedState: `${ExpandedState}`,
  ) => void;
  uncheckAllJobs: () => void;
};

const FilesPageContext = createContext<FilesPageContextType>({
  approvableJobs: [],
  checkedJobIds: [],
  defaultSelectedTab: DefaultSelectedTab.JOBS,
  getJobCheckedState: () => false,
  getJobsInOrder: () => [],
  getOrderCheckedState: () => false,
  orderCardsExpandedState: DEFAULT_ORDER_CARDS_EXPANDED_STATE,
  reportJobsLoaded: noop,
  reset: noop,
  setDefaultSelectedTab: noop,
  toggleAllOrderCardsExpandedState: noop,
  toggleJobCheck: noop,
  toggleOrderCheck: noop,
  toggleSingleOrderCardsExpandedState: noop,
  uncheckAllJobs: noop,
});

function FilesPageContextProvider({ children }: React.PropsWithChildren) {
  const queryClient = useQueryClient();
  const { userStore } = useStores();
  const userPermissions = userStore.getPermissions();

  const {
    orderCardsExpandedState,
    clearOrderCardsOverrides,
    toggleAllOrderCardsExpandedState,
    toggleSingleOrderCardsExpandedState,
  } = useOrderCardsExpandedState(LOCAL_STORAGE_KEY_EXPANDED_STATE);
  const [defaultSelectedTab, setDefaultSelectedTabState] =
    useState<`${DefaultSelectedTab}`>(getStoredDefaultTab());

  const [checkedJobIds, setCheckedJobIds] = useState<string[]>([]);
  const [loadedJobs, setLoadedJobs] = useState<Job[]>([]);

  const getJobsInOrder = useCallback((order: Order) => {
    const jobsQueryKey = makeJobsQueryContextKey(order.id);

    return queryClient.getQueryData<Job[]>([jobsQueryKey]) ?? [];
  }, []);

  const approvableJobs = useMemo(() => {
    return loadedJobs.filter((job) => {
      const { isApprovable: jobApprovable } = getApprovalStatus(
        job,
        userPermissions,
        Boolean(job.order?.editable),
      );
      return jobApprovable;
    });
  }, [loadedJobs, userPermissions]);

  const uncheckAllJobs = useCallback<FilesPageContextType['uncheckAllJobs']>(() => {
    setCheckedJobIds([]);
  }, []);

  const getJobCheckedState = useCallback<FilesPageContextType['getJobCheckedState']>(
    (job) => {
      return checkedJobIds.includes(job.id);
    },
    [checkedJobIds],
  );

  const getOrderCheckedState = useCallback<FilesPageContextType['getOrderCheckedState']>(
    (order) => {
      const jobsInOrder = getJobsInOrder(order).map((job) => job.id);
      const checkedJobsInOrder = checkedJobIds.filter((jobId) => {
        return jobsInOrder.includes(jobId);
      });

      const noJobsChecked = checkedJobsInOrder.length === 0;

      if (noJobsChecked) {
        return false;
      }

      const allJobsChecked = checkedJobsInOrder.length === jobsInOrder.length;

      if (allJobsChecked) {
        return true;
      }

      return 'intermediate';
    },
    [getJobsInOrder, checkedJobIds],
  );

  const reportJobsLoaded = useCallback<FilesPageContextType['reportJobsLoaded']>(
    (jobs) => {
      setLoadedJobs((state) => uniqBy([...jobs, ...state], (job) => job.id));
    },
    [],
  );

  const reset = useCallback(() => {
    uncheckAllJobs();
    clearOrderCardsOverrides();
  }, [uncheckAllJobs, clearOrderCardsOverrides]);

  const setDefaultSelectedTab = useCallback((tab: `${DefaultSelectedTab}`) => {
    setDefaultSelectedTabState(tab);
    localStorage.setItem(LOCAL_STORAGE_KEY_DEFAULT_TAB, tab);
  }, []);

  const toggleJobCheck = useCallback<FilesPageContextType['toggleJobCheck']>((job) => {
    setCheckedJobIds((state) => xor(state, [job.id]));
  }, []);

  const toggleOrderCheck = useCallback<FilesPageContextType['toggleOrderCheck']>(
    (order) => {
      setCheckedJobIds((state) => {
        const jobsInOrder = getJobsInOrder(order).map((job) => job.id);
        const checkedState = getOrderCheckedState(order);
        const shouldCheckAllJobs = checkedState === false;

        if (shouldCheckAllJobs) {
          return uniq([...state, ...jobsInOrder]);
        }

        return without(state, ...jobsInOrder);
      });
    },
    [getJobsInOrder, getOrderCheckedState],
  );

  return (
    <FilesPageContext.Provider
      value={{
        approvableJobs,
        checkedJobIds,
        defaultSelectedTab,
        getJobCheckedState,
        getJobsInOrder,
        getOrderCheckedState,
        orderCardsExpandedState,
        reportJobsLoaded,
        reset,
        setDefaultSelectedTab,
        toggleAllOrderCardsExpandedState,
        toggleJobCheck,
        toggleOrderCheck,
        toggleSingleOrderCardsExpandedState,
        uncheckAllJobs,
      }}
    >
      {children}
    </FilesPageContext.Provider>
  );
}

export default FilesPageContext;
export { FilesPageContextProvider, getStoredDefaultTab };
